import { useState, useRef, useEffect } from 'react';


export function useMouseEvents() {
    const [stageSize, setStageSize] = useState({ width: window.innerWidth, height: window.innerHeight - 100 });
    const [position, setPosition] = useState({ x: 50, y: 50 });
    const [scale, setScale] = useState(1);
    const [roomCreationDialogOpen, setRoomCreationDialogOpen] = useState(false);
    const lastPointerPos = useRef({ x: 0, y: 0 });
    const isDragging = useRef(false);

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setStageSize({
                width: window.innerWidth,
                height: window.innerHeight - 100
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);


    // Handle wheel zoom - 2% increments (less aggressive)
    const handleWheel = (e) => {
        e.evt.preventDefault();
        const stage = e.target.getStage();
        const pointerPos = stage.getPointerPosition();

        // Use 2% increments for smoother, less aggressive zooming
        const currentPercentage = scale * 100;
        const increment = 2; // 2% per scroll step

        // Determine direction and calculate new percentage
        let newPercentage;
        if (e.evt.deltaY > 0) {
            // Zoom out
            newPercentage = currentPercentage - increment;
        } else {
            // Zoom in
            newPercentage = currentPercentage + increment;
        }

        // Clamp between 10% and 500%
        newPercentage = Math.max(10, Math.min(500, newPercentage));
        const clampedScale = newPercentage / 100;

        const mousePointTo = {
            x: (pointerPos.x - position.x) / scale,
            y: (pointerPos.y - position.y) / scale
        };

        const newPos = {
            x: pointerPos.x - mousePointTo.x * clampedScale,
            y: pointerPos.y - mousePointTo.y * clampedScale
        };

        setScale(clampedScale);
        setPosition(newPos);
    };

    // Handle drag
    const handleMouseDown = (e) => {
        // Don't start canvas pan if clicking on a draggable element (like a room)
        let node = e.target;
        while (node) {
            if (node.draggable && node.draggable()) {
                isDragging.current = false;
                return;
            }
            node = node.getParent();
        }
        isDragging.current = true;
        lastPointerPos.current = {
            x: e.evt.clientX,
            y: e.evt.clientY
        };
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;

        const newPos = {
            x: position.x + (e.evt.clientX - lastPointerPos.current.x),
            y: position.y + (e.evt.clientY - lastPointerPos.current.y)
        };

        setPosition(newPos);
        lastPointerPos.current = {
            x: e.evt.clientX,
            y: e.evt.clientY
        };
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    // Handle touch gestures
    const handleTouchStart = (e) => {
        if (e.evt.touches.length === 1) {
            isDragging.current = true;
            lastPointerPos.current = {
                x: e.evt.touches[0].clientX,
                y: e.evt.touches[0].clientY
            };
        }
    };

    const handleTouchMove = (e) => {
        if (e.evt.touches.length === 1 && isDragging.current) {
            const newPos = {
                x: position.x + (e.evt.touches[0].clientX - lastPointerPos.current.x),
                y: position.y + (e.evt.touches[0].clientY - lastPointerPos.current.y)
            };
            setPosition(newPos);
            lastPointerPos.current = {
                x: e.evt.touches[0].clientX,
                y: e.evt.touches[0].clientY
            };
        } else if (e.evt.touches.length === 2) {
            // Pinch zoom - TODO: implement full pinch zoom functionality
            // const touch1 = e.evt.touches[0];
            // const touch2 = e.evt.touches[1];
            // const distance = Math.hypot(
            //   touch2.clientX - touch1.clientX,
            //   touch2.clientY - touch1.clientY
            // );
            // Store initial distance for pinch calculation
        }
    };

    const handleTouchEnd = () => {
        isDragging.current = false;
    };

    return {
        stageSize,
        position,
        scale,
        setPosition,
        setScale,
        isDragging,
        lastPointerPos,
        roomCreationDialogOpen,
        setRoomCreationDialogOpen,
        handleWheel,
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleTouchStart,
        handleTouchMove,
        handleTouchEnd,
    };
}